import { useId, useMemo, useState } from 'react';
import { DialogContent, Skeleton } from '@mui/material';
import LegOrderHeader from '@components/Modals/Headers/LegOrderHeader';
import FormikForm from './FormikForm';
import { useFetchLegQuery } from '@api/Trinium/Endpoints/Leg';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import FormikModal from '@components/Modals/FormikModal';
import { Formik, FormikHelpers } from 'formik';
import { ManagePickupDeliveryFormik, ManagePickupDeliveryProps, managePudlApiFieldMappings } from './types';
import { getObjectChanges } from '@/Infrastructure/Utilities/object.type';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import { mngPuDlToMutableLeg } from '@/ManagePickupDelivery/maps';
import { mapLegToAppointmentForm } from '@forms/Appointment/types';
import { mapLegToDriverForm } from '@forms/Driver/types';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import { useModalProvider } from '@components/Modals/ModalContext';
import { setFormFieldError } from '@forms/Components/types';
import { SendLegMutationErrorResponse, sendLegMutations } from '@api/Trinium/Endpoints/Leg/actions';
import FormikModalHeaderSkeleton from '@components/Modals/FormikModalHeaderSkeleton';
import LegOrderHeaderSkeleton from '@components/Modals/Headers/LegOrderHeaderSkeleton';
import AppointmentFormSkeleton from '@forms/Appointment/FormSkeleton';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';

/**
 * Local function used for rendering the content of the Pickup Delivery modal
 */
export default function Modal(props: ManagePickupDeliveryProps) {
	const formId = useId();
	const [isOpen, setIsOpen] = useState(true);
	const { number: terminalNumber } = useCurrentTerminal();
	const modalContext = useModalProvider();
	const { queueModalAnnouncement } = modalContext;
	const appDispatch = useAppDispatch();

	const { data: leg, isFetching } = useFetchLegQuery(
		{
			terminalNumber: terminalNumber,
			id: props.legId
		},
		{ refetchOnMountOrArgChange: true }
	);

	async function onFormSubmit(values: ManagePickupDeliveryFormik, formikHelpers: FormikHelpers<ManagePickupDeliveryFormik>) {
		const validationErrors = await formikHelpers.validateForm(values);
		if (leg && isFormikErrorsEmpty(validationErrors) && initialValues) {
			const valuesToPost: Partial<ManagePickupDeliveryFormik> = getObjectChanges(values, initialValues);

			const asMutableLeg = {
				id: leg.id,
				...mngPuDlToMutableLeg(valuesToPost, initialValues.assignedDriver?.driver?.id)
			};

			await sendLegMutations(asMutableLeg, managePudlApiFieldMappings)
				.then(() => {
					/** Show Reminder Modal if Leg's isReminder is true and Driver is being assigned */
					if (values.requiresReminder && values.assignedDriver?.driver?.id) {
						appDispatch(
							queueModal({
								Component: 'reminderModal',
								Props: {
									legId: props.legId,
									onCloseExtension: () => {
										setIsOpen(false);
									}
								}
							})
						);
					} else {
						setIsOpen(false);
					}
				})
				.catch(
					// eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
					({ errors }: SendLegMutationErrorResponse) => {
						errors.forEach((error) => {
							if (error.fieldName) {
								setFormFieldError(error.fieldName, error.errorMessage, formikHelpers.setFieldError);
							} else {
								queueModalAnnouncement({
									message: error.errorMessage,
									severity: 'error'
								});
							}
						});
					}
				);
		}
	}

	const initialValues: ManagePickupDeliveryFormik | undefined = useMemo(() => {
		if (!leg || isFetching) return undefined;

		return {
			pickupForm: mapLegToAppointmentForm(
				leg.scheduledPickupDateFrom?.unix,
				leg.scheduledPickupDateTo?.unix,
				leg.pickupDate?.unix,
				leg.pickupLocation
			),
			deliveryForm: mapLegToAppointmentForm(
				leg.scheduledDeliveryDateFrom?.unix,
				leg.scheduledDeliveryDateTo?.unix,
				leg.delvDate?.unix,
				leg.deliveryLocation
			),
			assignedDriver: mapLegToDriverForm(leg),
			requiresReminder: leg.isReminder ?? false
		};
	}, [leg, isFetching]);

	return (
		<FormikModal
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
			}}
		>
			{initialValues ? (
				<Formik
					initialValues={initialValues}
					onSubmit={onFormSubmit}
				>
					<>
						<FormikModalHeader
							formName={formId}
							onClose={() => {
								setIsOpen(false);
							}}
							title='Manage Order + Driver'
						/>

						<LegOrderHeader
							terminalNumber={terminalNumber}
							legId={props.legId}
						/>

						<DialogContent>
							{isFetching || !leg ? (
								<Skeleton
									variant='rounded'
									width={200}
									height={100}
								/>
							) : (
								<FormikForm
									id={formId}
									leg={leg}
								/>
							)}
						</DialogContent>
					</>
				</Formik>
			) : (
				<>
					<FormikModalHeaderSkeleton
						title='Manage Order + Driver'
						onClose={() => {
							setIsOpen(false);
						}}
					/>
					<LegOrderHeaderSkeleton />
					<DialogContent>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								rowGap: '8px'
							}}
						>
							<div style={{ display: 'flex' }}>
								<AppointmentFormSkeleton legend='Pickup' />
								<AppointmentFormSkeleton legend='Delivery' />
							</div>
							<Skeleton
								variant='rounded'
								width={350}
								height={60}
							/>
						</div>
					</DialogContent>
				</>
			)}
		</FormikModal>
	);
}
